import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import IsLoading from "../components/IsLoading";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import { formatDateAndTimeOnline } from "../components/DateAndTimeFunctions";
import CustomDateInputs from "../components/CustomDateInputs";
import AqiDisplay from "../components/AqiDisplay";
import "react-datepicker/dist/react-datepicker.css";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import { fetchInfo } from "../store/information";

const TestStationFullDataPaginated = () => {
  const dispatch = useDispatch();
  const isCompany = useSelector((state) => state.login.isCompany);

  useEffect(() => {
    const fetchData = () => {
      dispatch(fetchInfo());
    };

    fetchData();

    const intervalId = setInterval(fetchData, 900000); // 900000 ms = 15 minutes

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [dispatch]);

  const { theRouteUrl } = useParams();
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const baseURL = process.env.REACT_APP_BASE_URL;

  const theSpecificStation = useMemo(
    () =>
      Array.isArray(informationData)
        ? informationData.find((x) => x.slug === theRouteUrl)
        : null,
    [informationData, theRouteUrl, informationLoading, informationError]
  );

  const [device, setDevice] = useState();
  const [deviceID, setDeviceID] = useState();
  const [isKit, setIsKit] = useState(false);
  const [isOPM, setIsOpm] = useState();
  const [excelAPI, setExcelAPI] = useState("");
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [count, setCount] = useState(0);
  const [sensorType, setSensorType] = useState("pollution");
  const [pageIsValid, setPageIsValid] = useState(true);

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );

  useEffect(() => {
    if (Array.isArray(informationData) && informationData.length > 0) {
      setPageIsValid(!!theSpecificStation);
    }
  }, [theSpecificStation, informationData]);

  const apiUrls = useMemo(() => {
    if (theSpecificStation) {
      const { device, device_id, is_opm, is_kit } = theSpecificStation;

      setIsOpm(is_opm);
      setIsKit(is_kit);
      setDevice(device);
      setDeviceID(device_id);

      return {
        sapa: `${baseURL}test/${device.toUpperCase()}/${device_id}?start_date=${formatDateAndTimeOnline(
          selectedStartDateAndTime
        )}&end_date=${formatDateAndTimeOnline(
          selectedEndDateAndTime
        )}&page=${pageNumber}`,

        kit: `${baseURL}test/KITHAVA/${device_id}?start_date=${formatDateAndTimeOnline(
          selectedStartDateAndTime
        )}&end_date=${formatDateAndTimeOnline(
          selectedEndDateAndTime
        )}&page=${pageNumber}`,
      };
    }
    return {};
  }, [
    baseURL,
    theSpecificStation,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
    pageNumber,
  ]);

  const fetchData = useCallback(
    async (url, setData, setTotalData, setTotalPage, setError, setLoading) => {
      try {
        const response = await useJWTInterceptors.get(url);
        setData(response.data.results);
        setTotalData(response.data.count || 0);
        setTotalPage(Math.ceil((response.data.count || 0) / 50));
        setError(false);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const useFetchData = (url) => {
    const [data, setData] = useState([]);
    const [totalData, setTotalData] = useState(0);
    const [totalPage, setTotalPage] = useState(0);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      fetchData(url, setData, setTotalData, setTotalPage, setError, setLoading);
    }, [pageNumber, count]);

    return { data, totalData, totalPage, error, loading };
  };

  useEffect(() => {
    const generateExcelAPIUrl = () => {
      let deviceName;
      if (sensorType === "pollution") {
        deviceName = device.toLowerCase();
      } else if (sensorType === "environment") {
        deviceName = "kit";
      }
      const startDate = formatDateAndTimeOnline(selectedStartDateAndTime);
      const endDate = formatDateAndTimeOnline(selectedEndDateAndTime);

      setExcelAPI(
        `${baseURL}${deviceName}_excel/?device_id=${deviceID}&start_date=${startDate}&end_date=${endDate}&device_type=${
          device === "SAPA" ? 1 : 0
        }&granularity=10`
      );
    };

    generateExcelAPIUrl();
  }, [
    theSpecificStation,
    sensorType,
    selectedStartDateAndTime,
    selectedEndDateAndTime,
  ]);

  const {
    data: sapaPageData,
    totalData: totalSAPADataNumber,
    totalPage: totalSAPAPageNumber,
    loading: sapaLoading,
    error: sapaError,
  } = useFetchData(apiUrls.sapa);
  const {
    data: kitPageData,
    totalData: totalKitDataNumber,
    totalPage: totalKitPageNumber,
    loading: kitLoading,
    error: kitError,
  } = useFetchData(apiUrls.kit);

  const { currentPageData, totalPageNumber, loading, error } = useMemo(() => {
    switch (sensorType) {
      case "pollution":
        return {
          currentPageData: sapaPageData,
          totalPageNumber: totalSAPAPageNumber,
          loading: sapaLoading,
          error: sapaError,
        };
      case "environment":
        return {
          currentPageData: kitPageData,
          totalPageNumber: totalKitPageNumber,
          loading: kitLoading,
          error: kitError,
        };
      default:
        return {
          currentPageData: [],
          totalPageNumber: 0,
          loading: false,
          error: false,
        };
    }
  }, [
    sensorType,
    sapaPageData,
    sapaLoading,
    sapaError,
    totalSAPAPageNumber,
    kitPageData,
    totalKitPageNumber,
    kitLoading,
    kitError,
    count,
  ]);

  const downloadExcelReport = async () => {
    setExcelLoading(true);

    try {
      // Make a GET request to the server to fetch the Excel file
      const response = await useJWTInterceptors.get(excelAPI, {
        responseType: "blob", // set responseType to 'blob' for binary data
      });

      // Create a blob from the binary data received in the response
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      // Create a URL for the blob and use it to create a link element
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Set the link's properties for download
      link.href = url;
      link.download = `${formatDateAndTimeOnline(
        selectedStartDateAndTime
      )}_${formatDateAndTimeOnline(selectedEndDateAndTime)}.xlsx`; // specify the desired file name

      // Append the link to the document body and trigger a click to start download
      document.body.appendChild(link);
      link.click();

      // Clean up the created elements after the download is initiated
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // console.error("Error downloading Excel report:", error);
      setExcelError(error);
      // Handle the error (e.g., show a user-friendly message)
    } finally {
      setExcelLoading(false);
    }
    // console.log("excelLoading:", excelLoading);
  };

  const convertToJalali = (x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR").format(C);
    const E = D.concat(" ", "(", B, ")");
    return E;
  };

  if (!isCompany) {
    return <NoAccessPage />;
  } else if (loading && isCompany) {
    return <IsLoading />;
  } else if (currentPageData && isCompany && sensorType === "environment") {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="test"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setCount={setCount}
            count={count}
            excelLoading={excelLoading}
            downloadExcelReport={downloadExcelReport}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            sensorType={sensorType}
            isKit={isKit}
            setSensorType={setSensorType}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {currentPageData.length > 0 ? (
                <PaginationButtonToolbar
                  setSelectedStartDateAndTime
                  pageNumber={pageNumber}
                  totalPageNumber={totalKitPageNumber}
                  setPageNumber={setPageNumber}
                  sensorType={sensorType}
                />
              ) : (
                ""
              )}
            </div>
            {Array.isArray(currentPageData) && currentPageData.length > 0 ? (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      <>
                        <th className=" p-3">SPL</th>
                        <th className=" p-3">VOC</th>
                        <th className=" p-3">Pressure</th>
                        <th className=" p-3">تاریخ و ساعت</th>
                      </>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageData.map((station) => (
                      <tr key={station.ccreated_datetime}>
                        {/* pm10 is spl in api */}
                        <td>
                          {station.pm10 !== null &&
                          station.pm10 !== undefined ? (
                            <p>
                              {Number(station.pm10).toFixed(2)}
                              <sub>dB</sub>
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </td>

                        {/* pm25 is voc in api */}
                        <td>
                          {station.pm25 !== null &&
                          station.pm25 !== undefined ? (
                            <p>
                              {Number(station.pm25).toFixed(2)}
                              <sub>ppb</sub>
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </td>

                        {/* no2 is pressure in api */}
                        <td>
                          {station.no2 !== null && station.no2 !== undefined ? (
                            <p>
                              {Number(station.no2).toFixed(2)}
                              <sub>kPa</sub>
                            </p>
                          ) : (
                            <p>-</p>
                          )}
                        </td>

                        {station.ccreated_datetime ? (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.ccreated_datetime)}
                          </td>
                        ) : (
                          <td className="ss01" dir="ltr">
                            {convertToJalali(station.created_datetime)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  } else if (currentPageData && isCompany) {
    return (
      <div className="container-fluid p-2 text-medium">
        <div className="row">
          <CustomDateInputs
            type="test"
            startDateAndTime={selectedStartDateAndTime}
            endDateAndTime={selectedEndDateAndTime}
            setSelectedStartDateAndTime={setSelectedStartDateAndTime}
            setSelectedEndDateAndTime={setSelectedEndDateAndTime}
            setCount={setCount}
            count={count}
            excelLoading={excelLoading}
            downloadExcelReport={downloadExcelReport}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            sensorType={sensorType}
            isKit={isKit}
            setSensorType={setSensorType}
          />
          <div className="col-lg-9 col-xl-10 " style={{ overflowX: "scroll" }}>
            <div dir="ltr" className="mb-1 d flex">
              {currentPageData.length > 0 ? (
                <PaginationButtonToolbar
                  setSelectedStartDateAndTime
                  pageNumber={pageNumber}
                  totalPageNumber={totalPageNumber}
                  setPageNumber={setPageNumber}
                  sensorType={sensorType}
                />
              ) : (
                ""
              )}
            </div>
            {Array.isArray(currentPageData) && currentPageData.length > 0 ? (
              <div className="bg-secondary p-1">
                <table
                  id="dtBasicExample"
                  className="table bg-light table-striped table-bordered table-sm mx-auto text-center"
                  style={{ overflowX: "scroll", maxWidth: "screen" }}
                  cellSpacing="0"
                  dir="rtl"
                  width="90%"
                >
                  <thead>
                    <tr>
                      {device === "BNZN" ? (
                        <>
                          <th className=" p-3">B</th>
                          <th className=" p-3">CO</th>
                          <th className=" p-3">Temp</th>
                          <th className=" p-3">Press</th>
                        </>
                      ) : (
                        ""
                      )}

                      {device === "OSPM" ||
                      device === "ESPS" ||
                      device === "SAPA" ? (
                        <>
                          <th className=" p-3">شاخص AQI</th>
                          <th className=" p-3">آلاینده عامل</th>
                        </>
                      ) : (
                        ""
                      )}
                      {device === "SATA" || device === "SAPA" ? (
                        <>
                          <th className=" p-3">CO</th>

                          <th className=" p-3">O3</th>

                          <th className=" p-3">
                            NO<sub>2</sub>
                          </th>

                          <th className=" p-3">
                            SO<sub>2</sub>
                          </th>
                        </>
                      ) : (
                        ""
                      )}

                      {device === "OPSGA" ? (
                        <>
                          <th className="small p-1 p-md-3">O2</th>
                          <th className="small p-1 p-md-3">CO</th>
                          <th className="small p-1 p-md-3">NO</th>
                          <th className="small p-1 p-md-3">NO2</th>
                          <th className="small p-1 p-md-3">SO2</th>
                          <th className="small p-1 p-md-3">Flow</th>
                        </>
                      ) : (
                        ""
                      )}

                      {device !== "OSPL" &&
                      device !== "BNZN" &&
                      device !== "OPSGA" ? (
                        <>
                          <th className=" p-3">PM2.5</th>
                          <th className=" p-3">PM10</th>
                        </>
                      ) : (
                        ""
                      )}

                      {device === "OSPL" ? <th className=" p-3">SPL</th> : ""}

                      {device === "BNZN" || device === "OPSGA" ? (
                        ""
                      ) : (
                        <th className=" p-3">دما</th>
                      )}

                      {device === "BNZN" || device === "OPSGA" ? (
                        ""
                      ) : (
                        <th className=" p-3">رطوبت</th>
                      )}

                      <th className=" p-3">تاریخ و ساعت</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentPageData.map((station) => {
                      const hasData =
                        station.pm10 ||
                        station.B ||
                        station.pm25 ||
                        station.no ||
                        station.co ||
                        station.created_datetime;

                      if (!hasData) return null;

                      return (
                        <tr key={station.created_datetime}>
                          {device === "OSPM" || device === "ESPS" ? (
                            <>
                              {station.AQI != null ? ( // Check for null or undefined
                                <td className="text-center px-md-3 align-baseline">
                                  <AqiDisplay AQI={station.AQI} />
                                </td>
                              ) : null}

                              {station.mainPollutant != null ||
                              station.AQI === 0 ? ( // Combined condition
                                <td className="text-center px-md-5 align-baseline">
                                  {station.mainPollutant}
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {(station.pm25 === null ||
                                station.pm25 === undefined) &&
                              station.pm25 !== 0 ? (
                                <td>-</td>
                              ) : (
                                <td>
                                  <p>
                                    {Number(station.pm25).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              )}

                              {(station.pm10 === null ||
                                station.pm10 === undefined) &&
                              station.pm10 !== 0 ? (
                                <td>-</td>
                              ) : (
                                <td>
                                  <p>
                                    {Number(station.pm10).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              )}

                              {(station.temperature === null ||
                                station.temperature === undefined) &&
                              station.temperature !== 0 ? (
                                <td>-</td>
                              ) : (
                                <td>
                                  <p>
                                    {Number(station.temperature).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>℃</sub>
                                  </p>
                                </td>
                              )}

                              {(station.humidity === null ||
                                station.humidity === undefined) &&
                              station.humidity !== 0 ? (
                                <td>-</td>
                              ) : (
                                <td>
                                  <p>
                                    {Number(station.humidity).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>RH%</sub>
                                  </p>
                                </td>
                              )}

                              {station.ccreated_datetime ? (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.ccreated_datetime)}
                                </td>
                              ) : (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.created_datetime)}
                                </td>
                              )}
                            </>
                          ) : null}

                          {device === "SAPA" ? (
                            <>
                              {station.AQI != null ? ( // Check for null or undefined
                                <td className="text-center px-md-3 align-baseline">
                                  <AqiDisplay AQI={station.AQI} />
                                </td>
                              ) : null}

                              {station.mainPollutant != null ||
                              station.AQI === 0 ? ( // Combined condition
                                <td className="text-center px-md-5 align-baseline">
                                  {station.mainPollutant}
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.co !== null &&
                              station.co !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.co).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.o3 !== null &&
                              station.o3 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.o3).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.no2 !== null &&
                              station.no2 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.no2).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.so2 !== null &&
                              station.so2 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.so2).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.pm25 !== null &&
                              station.pm25 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.pm25).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.pm10 !== null &&
                              station.pm10 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.pm10).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.temperature !== null &&
                              station.temperature !== undefined ? (
                                <td>
                                  <p>
                                    <sub>℃</sub>

                                    {Number(station.temperature).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.humidity !== null &&
                              station.humidity !== undefined ? (
                                <td>
                                  <p>
                                    <span>
                                      {Number(station.humidity).toFixed(2)}
                                    </span>
                                    <sub>%RH</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.ccreated_datetime ? (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.ccreated_datetime)}
                                </td>
                              ) : (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.created_datetime)}
                                </td>
                              )}
                            </>
                          ) : null}

                          {device === "OSPL" ? (
                            <>
                              {/* spl is pm 10 in api */}
                              {station.pm10 !== null &&
                              station.pm10 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.pm10).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                    <sub>dB</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.temperature !== null &&
                              station.temperature !== undefined ? (
                                <td>
                                  <p>
                                    <sub>℃</sub>

                                    {Number(station.temperature).toFixed(
                                      device === "SAPA" ? 0 : 2
                                    )}
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.humidity !== null &&
                              station.humidity !== undefined ? (
                                <td>
                                  <p>
                                    <span>
                                      {Number(station.humidity).toFixed(2)}
                                    </span>
                                    <sub>%RH</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.ccreated_datetime ? (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.ccreated_datetime)}
                                </td>
                              ) : (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.created_datetime)}
                                </td>
                              )}
                            </>
                          ) : null}

                          {device === "OPSGA" ? (
                            <>
                              {/* o2 is o3 in api */}
                              {station.o3 !== null &&
                              station.o3 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.o3)}
                                    <sub>%Vol</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.co !== null &&
                              station.co !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.co).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {/* no is pm10 in api */}
                              {station.pm10 !== null &&
                              station.pm10 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.pm10)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.no2 !== null &&
                              station.no2 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.no2).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.so2 !== null &&
                              station.so2 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.so2).toFixed(2)}
                                    <sub>ppb</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {/* flow is pm25 in api */}
                              {station.pm25 !== null &&
                              station.pm25 !== undefined ? (
                                <td>
                                  <p>
                                    {Number(station.pm25).toFixed(2)}
                                    <sub>m3/s</sub>
                                  </p>
                                </td>
                              ) : (
                                <td>-</td>
                              )}

                              {station.ccreated_datetime ? (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.ccreated_datetime)}
                                </td>
                              ) : (
                                <td className="ss01" dir="ltr">
                                  {convertToJalali(station.created_datetime)}
                                </td>
                              )}
                            </>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
};

export default TestStationFullDataPaginated;
